import React, { useCallback, useEffect, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import moment from 'moment';
import { Button, FeaturedIcon, Modal } from '@myhealth-raccoon/components';

import { BookWithDoctorForm, DoctorBookingInformationForm } from "src/components/forms/BookWithDoctorForm";
import { useDebounce } from 'src/hooks/useDebounce';
import { Availability } from 'src/components/availability';
import { UserWithSiteModel } from 'src/services/user';
import { umbracoService } from 'src/services/umbraco';
import { useAsyncData } from '@myhealth-raccoon/services';
import { useDispatch } from 'src/store';
import { setAppointmentTypeId, setClinicId } from 'src/store/appointment';
import { doctorName } from 'src/utils/name';
import { captureException } from '@sentry/react';
import { useAppointmentTypeSelector, useClinicSelector } from 'src/hooks/useAsyncSelector';
import { AutomedDoctorAvailabilityTimeModel } from 'src/services/availability';
import { innerHtml } from 'src/utils/html';

type Props = {
    toggleAvailablilty?: boolean;
}

export function BookWithDoctor({ toggleAvailablilty }: Props) {
    const [showAvailability, setShowAvailability] = useState(false);
    const [isAvailablityDisabled, setIsAvailablityDisabled] = useState(false);
    const [doctorDisplayName, setDoctorDisplayName] = useState<string>();
    const [doctorIsDisplayOnly, setDoctorIsDisplayOnly] = useState<boolean>(false);
    const [isAlertShown, setIsAlertShown] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const openAlert = useCallback((message: string) => {
        setIsAlertShown(true);
        setAlertMessage(message);
    }, []);

    const sites = useLoaderData() as UserWithSiteModel[];
    const { data: umbracoSite } = useClinicSelector();
    const { data: appointmentType } = useAppointmentTypeSelector();

    const { invoke: getBookingToken, isLoading: isLoadingToken, ...tokenRequest } = useAsyncData({
        service: umbracoService,
        method: umbracoService.GetBookingToken,
        invokeOnMount: false
    });

    const [bookingQuery, setValues] = useState<DoctorBookingInformationForm>();
    const changeValues = useDebounce(useCallback((values: DoctorBookingInformationForm) => {
        setValues(values);
    }, []), 300);

    const onValidate = useCallback((valid: boolean) => {
        setIsAvailablityDisabled(!valid);
    }, []);

    const dispatch = useDispatch();
    useEffect(() => {
        if (bookingQuery?.siteId) {
            dispatch(setClinicId(bookingQuery?.siteId))
        }
        if (bookingQuery?.appointmentTypeId) {
            dispatch(setAppointmentTypeId(bookingQuery?.appointmentTypeId))
        }
    }, [bookingQuery, dispatch])

    const openApp = useCallback(async (date: Date, timeSlot: AutomedDoctorAvailabilityTimeModel) => {
        const newWindow = window.open();
        try {
            const { access_token } = await getBookingToken();
            const query = new URLSearchParams({
                dateTime: moment(date).toISOString(),
                doctor: bookingQuery!.doctorId.toString(),
                clinic: bookingQuery!.siteId.toString(),
                appointment: bookingQuery!.appointmentTypeId,
                new: (bookingQuery!.patientType === "new").toString(),
                _booking: access_token
            });
            query.append('timeSlot.time', timeSlot.time);
            query.append('timeSlot.duration', timeSlot.duration);
            for (const [key, value] of [
                ['ab', timeSlot.ab],
                ['r1', timeSlot.r1],
                ['r2', timeSlot.r2],
                ['r3', timeSlot.r3],
                ['r4', timeSlot.r4]
            ]) {
                if (key && value) {
                    query.append(`timeSlot.${key}`, value);
                }
            }
            if (newWindow) {
                newWindow.location = `${window.location.origin}/app?${query}`;
            }

            if (!newWindow || newWindow.closed) {
                captureException("could not open booking app");
            }
        } catch (e) {
            newWindow?.close();
            captureException(e);
        }
    }, [bookingQuery, getBookingToken]);

    return <div className='space-y-4 pt-6 pb-5 bg-white rounded-xl border border-gray-200'>
        <h3 className='px-4'>
            {doctorDisplayName && <span className='block font-semibold text-lg text-gray-900'>Book with {doctorDisplayName}</span>}
            <small className='block mt-1 font-light text-sm text-gray-600'>Your selections will help show the right availability.</small>
        </h3>

        <hr />

        <BookWithDoctorForm
            className='px-4'
            clinics={sites}
            onNewValues={changeValues}
            onValidate={onValidate}
            defaultValues={{
                siteId: sites?.[0]?.siteId,
                appointmentMode: 'faceToFace',
                patientType: 'existing'
            }}
            onDoctorDetails={(doc) => {
                setDoctorDisplayName(doctorName(doc));
                setDoctorIsDisplayOnly(doc.displayProfileNoOnlineBooking);
            }}
        />
        {(toggleAvailablilty && !showAvailability)
            ? <div className="px-4">
                <Button onClick={() => {
                    setShowAvailability(true)
                }} className='w-full'>View availability</Button>
            </div>
            : bookingQuery && bookingQuery.doctorId && appointmentType && <Availability
                disabled={isLoadingToken || isAvailablityDisabled || doctorIsDisplayOnly}
                doctorId={bookingQuery.doctorId}
                appointmentType={appointmentType}
                clinicId={Number(bookingQuery.siteId)}
                onSelectTimeSlot={({ date, timeSlot }) => {
                    if (appointmentType.displayOnly === "1") {
                        openAlert(appointmentType.displayOnlyMessage);
                    } else {
                        openApp(date, timeSlot);
                    }
                }}
                errorElement={doctorIsDisplayOnly
                    ? <>
                        <p><strong>This provider does not accept online bookings.</strong></p>
                        <p>Please contact the centre to make an appointment on{' '}
                            <a className="text-primary" href={`tel:${umbracoSite?.phoneNumber}`} >{umbracoSite?.phoneNumber}</a>.</p>
                    </>
                    : undefined}
                futureApptWeeks={umbracoSite?.futureApptWeeks}
            />
        }
        {tokenRequest?.error && <div className='text-error font-medium text-base px-4'>{tokenRequest.error.message || tokenRequest.error.name}</div>}
        <Modal
            backdropClassname='opacity-0'
            open={isAlertShown}
            onClose={() => setIsAlertShown(false)}
            size='sm'
            title={<FeaturedIcon />}
        >
            <div className='prose-sm' {...innerHtml(alertMessage)} />
        </Modal>
    </div>
}